import type { GameState } from '../../game-core/src/types.ts';
import type { LoadedSave, SaveDriver, SaveSlot } from './types.ts';
import { decodeSave, encodeSave } from './validation.ts';

const READ_ORDER: SaveSlot[] = ['primary', 'backup-1', 'backup-2'];

export class SaveRepository {
  private writeQueue: Promise<void> = Promise.resolve();
  private readonly driver: SaveDriver;

  constructor(driver: SaveDriver) {
    this.driver = driver;
  }

  async load(): Promise<LoadedSave | null> {
    for (const source of READ_ORDER) {
      const raw = await this.driver.read(source);
      if (!raw) continue;
      const envelope = decodeSave(raw);
      if (!envelope) continue;
      return {
        state: envelope.payload,
        source,
        recovered: source !== 'primary'
      };
    }
    return null;
  }

  save(state: GameState, now = Date.now()): Promise<void> {
    const operation = async () => {
      const [primary, backupOne] = await Promise.all([
        this.driver.read('primary'),
        this.driver.read('backup-1')
      ]);
      if (backupOne) await this.driver.write('backup-2', backupOne);
      if (primary) await this.driver.write('backup-1', primary);
      await this.driver.write('primary', encodeSave(state, now));
    };
    this.writeQueue = this.writeQueue.then(operation, operation);
    return this.writeQueue;
  }

  async clear(): Promise<void> {
    await Promise.all(READ_ORDER.map((slot) => this.driver.remove(slot)));
  }
}
